import { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import BasicCaption from '@fec/frontend-aron/elements/media/BasicCaption/BasicCaption';
import MediaBlockingNotice from '@fec/frontend-aron/elements/media/MediaBlockingNotice/MediaBlockingNotice';
import usePlayerAppEvents from '@fec/frontend-aron/foundation/hooks/usePlayerAppEvents';
import usePlayerIsBlocked from '@fec/frontend-aron/foundation/hooks/usePlayerIsBlocked';
import Image from '@fec/frontend/elements/media/image/Image';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon';
import MediaTimeIndicator from '@fec/frontend/elements/media/media-time-indicator/MediaTimeIndicator';
import usePlayerA11yHelper from '@fec/frontend-aron/foundation/hooks/usePlayerA11yHelper';

import '@fec/frontend-aron/modules/media/BasicVideo/BasicVideo.scss';

const BasicVideoApp = ({ asset, isTopMediaElement }) => {
  const { urn, duration = 0, image = null, bridgePayload = null } = asset;
  const [isActive, setIsActive] = useState(false);
  const { isBlocked, reason } = usePlayerIsBlocked(asset);
  const { a11yAttributes } = usePlayerA11yHelper(asset, isBlocked, isActive);

  const {
    dispatchMediaClickedEvents,
    dispatchMediaStoppedEvents,
    hasActivePlayer,
  } = usePlayerAppEvents(urn, bridgePayload);

  useEffect(() => {
    setIsActive(hasActivePlayer);
  }, [hasActivePlayer]);

  const handleInteraction = () => {
    if (isBlocked) {
      return;
    }
    if (isActive) {
      dispatchMediaStoppedEvents();
    } else {
      dispatchMediaClickedEvents();
    }
  };

  const handleInteractionByKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleInteraction();
    }
  };

  return (
    <div
      className={classNames('basic-video', 'basic-video--app', {
        'basic-video--active': isActive,
        'js-player-active': isActive, // inform non-react components (i.e. TME container) about player state (handled via CSS)
      })}
      data-app-video
      data-bridge-payload={JSON.stringify(bridgePayload)}
      {...a11yAttributes}
      onClick={() => handleInteraction()}
      onKeyDown={(e) => handleInteractionByKey(e)}>
      {!isActive && (
        <div className="basic-video__image-container" aria-hidden="true">
          <Image image={image} noBackground={isTopMediaElement} />
        </div>
      )}

      {!isBlocked && !isActive && (
        <div className="basic-video__duration" aria-hidden="true">
          <MediaTimeIndicator duration={duration} />
        </div>
      )}

      {/* a playing video in the app always has a Play/Stop button (unless the video is not playable) */}
      {!isBlocked && (
        <div className="basic-video__play-button" aria-hidden="true">
          <PlayIcon type="video" isActive={isActive} />
        </div>
      )}

      <div className="basic-video__player-container"></div>

      {isBlocked && (
        <div className="basic-video__notice">
          <MediaBlockingNotice asset={asset} reason={reason} />
        </div>
      )}

      <div className="basic-video__caption" onClick={() => handleInteraction()}>
        <BasicCaption asset={asset} isActive={isActive} />
      </div>
    </div>
  );
};

BasicVideoApp.propTypes = {
  asset: PropTypes.object,
  isTopMediaElement: PropTypes.bool,
};

export default BasicVideoApp;
